<template>
  <div v-if="ready" id="learner-dashboard-landing">
    <div class="heading-container content-with-spacing">
      <div class="title-container">
        <h1 id="learner-dashboard-heading">{{ programmeDetails.name }}</h1>
      </div>
      <div v-if="availableProgrammes.length > 1" class="header-dropdown">
        <span class="dropdown-label" aria-hidden="true">Programmes:</span>
        <k-dropdown
          :options="availableProgrammes"
          v-model="currentProgrammeId"
          aria-label="Select programme"
        ></k-dropdown>
      </div>
    </div>
    <main class="content-with-spacing panel-columns" aria-labelledby="learner-dashboard-heading">
      <p class="sr-only" role="status" aria-live="polite">{{ programmeModulesStatusMessage }}</p>
      <p v-if="programmeModulesLoadError" class="sr-only" role="alert">{{ programmeModulesStatusMessage }}</p>
      <div v-if="currentProgrammeId" class="col-1">
        <calendar-events-panel
          :learnerInfo="learnerInfo"
          :programmeId="currentProgrammeId"
        ></calendar-events-panel>
        <meeting-notes-panel v-if="modulesWithReleaseDate.length > 0"
          :programmeId="currentProgrammeId"
        ></meeting-notes-panel>
        <contacts-panel
          :learnerInfo="learnerInfo"
          :programmeId="currentProgrammeId"
        ></contacts-panel>
        <learner-homepage-links-panel
          v-if="$featureFlags.isEnabled('learner-profile-links-app')"
          :programmeId="Number(currentProgrammeId)"
        ></learner-homepage-links-panel>
      </div>
      <div class="col-2">
        <template v-if="modulesWithReleaseDate.length > 0">
          <timeline-panel
            :learnerInfo="learnerInfo"
            :programmeId="currentProgrammeId"
            :programmeDetails="programmeDetails"
            :programmeModules="modulesWithReleaseDate"
          ></timeline-panel>
          <curriculum-panel
            :learnerInfo="learnerInfo"
            :programmeId="currentProgrammeId"
            :programmeModules="modulesWithReleaseDate"
            :programmeDetails="programmeDetails"
          ></curriculum-panel>
          <progress-otjs-panel
            v-if="isApprenticeship"
            :programmeId="currentProgrammeId"
            :userId="store.userId"
          ></progress-otjs-panel>
        </template>
        <empty-placeholder v-else
          title="No modules available in this programme"
          :info="emptyProgrammeText"
          :fa-icon="emptyIcon"
        ></empty-placeholder>
      </div>
    </main>
  </div>
</template>

<style>
#learner-dashboard-landing h3,
#learner-dashboard-landing .panel-header h2 {
  margin-top: 0;
  font-family: hk-grotesk-light, sans-serif;
}

#learner-dashboard-landing h3 {
  font-size: 1.2em;
}

#learner-dashboard-landing hr.horizontal-hr {
  margin: 20px 0;
}

#learner-dashboard-landing .module-section-header button:focus-visible,
#learner-dashboard-landing .section-toggle:focus-visible,
#learner-dashboard-landing .section-collapse-toggle:focus-visible,
#learner-dashboard-landing .section-collapse-toggle:focus,
#learner-dashboard-landing .module-section-header:focus-visible,
#learner-dashboard-landing .module-section-header:focus,
#learner-dashboard-landing .module-list a:focus-visible {
  outline: 2px solid currentcolor;
  outline-offset: 2px;
}

#learner-dashboard-landing .header-dropdown {
  display: flex;
  align-items: center;
  gap: 5px;
}

#learner-dashboard-landing .empty-placeholder-content {
  margin-top: 10vh;
}

@media (max-width: 768px) {
  #learner-dashboard-landing .empty-placeholder-content {
    margin-top: 5vh;
  }
}
</style>

<style scoped>
.header-dropdown {
  display: flex;
  align-items: center;
  gap: 10px;
}

.panel-columns {
  display: flex;
  flex-wrap: wrap;
  gap: 15px;
}

.col-1 {
  flex: 1;
}

.col-2 {
  flex: 2;
  max-width: 90vw;
  overflow: hidden;
}

@media (max-width: 992px) {
  .col-2 {
    overflow: visible;
    max-width: stretch;
  }
}
</style>

<script>
import { storeToRefs } from 'pinia';
import useGlobalStore from '@stores/global';
import useAppProgrammeStore from '@stores/app-programme-store';
import TimeMixin from '@mixins/time-mixins';
import PageReadyMixin from '@mixins/page-ready-mixin';
import KDropdown from '@base-components/k-dropdown.vue';
import EmptyPlaceholder from '@base-components/empty-placeholder.vue';
import ProgressOtjsPanel from './homepage_panels/progress-otjs-panel.vue';
import ContactsPanel from './homepage_panels/contacts-panel.vue';
import TimelinePanel from './homepage_panels/timeline-panel.vue';
import CalendarEventsPanel from './homepage_panels/calendar-events-panel.vue';
import CurriculumPanel from './homepage_panels/curriculum-panel.vue';
import MeetingNotesPanel from './homepage_panels/meeting-notes-panel.vue';
import LearnerHomepageLinksPanel from './homepage_panels/learner-homepage-links-panel.vue';

export default {
  components: {
    KDropdown,
    ProgressOtjsPanel,
    ContactsPanel,
    TimelinePanel,
    CalendarEventsPanel,
    CurriculumPanel,
    MeetingNotesPanel,
    LearnerHomepageLinksPanel,
    EmptyPlaceholder,
  },

  mixins: [TimeMixin, PageReadyMixin],

  data() {
    const programmeStore = useAppProgrammeStore();
    const { currentProgrammeId } = storeToRefs(programmeStore);

    return {
      store: useGlobalStore(),
      programmeStore,
      currentProgrammeId,
      isResolving: false,
      programmeModules: [],
      programmeModulesReady: false,
      programmeModulesLoadError: false,
      programmeModulesErrorMessage: '',
    };
  },

  created() {
    this.resolveProgrammeId();
  },

  beforeMount() {
    this.getProgrammeModules();
  },

  watch: {
    currentProgrammeId(newVal, oldVal) {
      if (!newVal) return;
      const currentRouteProgrammeId = Number(this.$route.params.programmeId);
      if (newVal !== currentRouteProgrammeId) {
        this.contentReady = false;
        this.$nextTick(() => {
          this.contentReady = true;
          // Update URL without triggering Vue Router navigation guards,
          // which would cancel all in-flight panel requests via $httpCancel()
          const { href } = this.$router.resolve({ name: 'home', params: { programmeId: newVal } });
          window.history.replaceState(window.history.state, '', href);
        });
      }
      if (oldVal != null && newVal !== oldVal) {
        this.getProgrammeModules();
      }
    },

    programmeDetails(newVal) {
      if (newVal) {
        this.registerCrumbs();
      }
    },

    programmeIdFromRoute(newVal) {
      if (!newVal) {
        this.resolveProgrammeId();
      }
    },
  },

  computed: {
    ready() {
      return this.currentProgrammeId && this.programmeModulesReady && this.contentReady;
    },
    learnerInfo() {
      return {
        coaches: this.store.userCoaches ?? [],
        data_mentors: this.store.userDataMentors ?? [],
        line_managers: this.store.userLineManagers ?? [],
      };
    },
    availableProgrammes() {
      return this.programmeStore.programmes;
    },
    programmeIdFromRoute() {
      const paramId = Number(this.$route.params.programmeId);
      return !Number.isNaN(paramId) ? paramId : undefined;
    },
    programmeDetails() {
      return this.programmeStore.getProgramme(this.programmeStore.currentProgrammeId);
    },
    programmeInfoReady() {
      return Boolean(this.programmeDetails && this.programmeStore.currentProgrammeId);
    },
    isApprenticeship() {
      return this.programmeDetails?.is_apprenticeship;
    },

    programmeScheduleLink() {
      return `<a href="/programmes/${this.currentProgrammeId}/schedule">Schedule Page</a>`;
    },

    emptyProgrammeText() {
      return `You can check the ${this.programmeScheduleLink} to see when each module will be released in this programme.
      <br>If you think there is an issue, please contact Cambridge Spark`;
    },

    emptyIcon() {
      return 'fa-duotone fa-hourglass-start';
    },
    programmeModulesStatusMessage() {
      if (!this.currentProgrammeId) {
        return '';
      }
      if (!this.programmeModulesReady) {
        return 'Loading programme modules.';
      }
      if (this.programmeModulesLoadError) {
        return this.programmeModulesErrorMessage || 'Unable to load programme modules right now.';
      }
      if (!this.programmeModules.length) {
        return 'No modules are currently available in this programme.';
      }
      return `${this.programmeModules.length} modules loaded for this programme.`;
    },

    modulesWithReleaseDate() {
      return this.programmeModules?.filter(m => m.release_date !== null);
    },
  },

  methods: {
    resolveProgrammeId() {
      if (this.isResolving) return;
      this.isResolving = true;
      this.$Loading.start();
      this.programmeStore.currentProgrammeId = null;
      this.$logger.info('Resolving programme ID from route or store');
      const newProgrammeId = this.programmeIdFromRoute || this.programmeStore.defaultProgrammeId;
      if (newProgrammeId) {
        if (this.programmeStore.isProgrammeAccessible(newProgrammeId)) {
          this.$logger.info('Programme ID resolved: ', { newProgrammeId });
          this.programmeStore.currentProgrammeId = newProgrammeId;
          this.contentReady = true;
        } else {
          this.$logger.info('User is not a member of the requested programme');
          this.$router.push({ name: '404' });
          this.programmeStore.currentProgrammeId = null;
        }
      } else {
        this.$logger.info('User is not a member of any programmes');
        this.$router.push('/user_not_in_programme');
        this.programmeStore.currentProgrammeId = null;
      }
      this.isResolving = false;
      this.$Loading.finish();
    },

    getProgrammeModules() {
      if (!this.currentProgrammeId) {
        return;
      }
      this.programmeModulesReady = false;
      this.programmeModulesLoadError = false;
      this.programmeModulesErrorMessage = '';
      this.$Loading.start();
      this.$logger.info('Getting modules for programme', {
        programmeId: this.currentProgrammeId,
      }, true);
      this.$http.get(`/api/curriculum/programmes/${this.currentProgrammeId}/modules`)
        .then(response => {
          this.programmeModules = response.data.modules;
          this.$logger.info('Got modules for programme', {
            programmeId: this.currentProgrammeId,
          });
        })
        .catch(err => {
          this.programmeModules = [];
          this.programmeModulesLoadError = true;
          if (this.$http.errIn(err, [404])) {
            this.$logger.warn('No modules found for programme', {
              programmeId: this.currentProgrammeId,
            });
            this.programmeModulesErrorMessage = 'No modules were found for this programme.';
            this.$ktoast.info('No modules were found for this programme', { position: 'top-center' });
          } else if (this.$http.isWarning(err)) {
            this.$logger.warn('Error while getting modules for programme', {
              programmeId: this.currentProgrammeId,
            }, err);
            this.programmeModulesErrorMessage = 'A temporary issue prevented loading programme modules.';
            this.$ktoast.showError(err, true);
          } else {
            this.$logger.error('Error while getting modules for programme', {
              programmeId: this.currentProgrammeId,
            }, err);
            this.programmeModulesErrorMessage = 'Unable to load programme modules right now.';
            this.$ktoast.showError(err, true);
          }
        })
        .then(() => {
          this.programmeModulesReady = true;
          this.$Loading.finish();
        });
    },

    registerCrumbs() {
      this.$crumbs.register([
        {
          text: this.programmeDetails.name,
          active: true,
        },
      ]);
    },
  },
};
</script>
